import { useNavigate } from '@tanstack/react-router';
import { Button } from '@workspace/ui/components/button';
import { Skeleton } from '@workspace/ui/components/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@workspace/ui/components/tabs';
import { ChevronDown, CreditCard, Plus } from 'lucide-react';
import { useAuth } from '~/auth/use-auth';
import { m } from '~/paraglide/messages.js';
import cop from '~/static/icons/cop.png';
import ksm from '~/static/icons/ksm.png';
import usd from '~/static/icons/usd.png';
import { CURRENCIES, getCurrencyKeyFromBalanceCode } from '~/utils/currency';
import { useAccounts } from '../../../-hooks/use-accounts';
import type { AccountsBancolombiaResponse } from '../../../accounts/-types/account';

const ASSETS = {
  DUSD: {
    title: m['card.account_balance_summary.tabs.usd'](),
    icon: usd,
  },
  KSM: {
    title: m['card.account_balance_summary.tabs.ksm'](),
    icon: ksm,
  },
  COP: {
    title: m['card.account_balance_summary.tabs.cop'](),
    icon: cop,
  },
} as const;

type Asset = keyof typeof ASSETS;
type AssetPair = `${Asset}/${number}`;
type BalanceInfo = {
  current: string;
  pending: string;
  in?: string;
  out?: string;
};

type Props = {
  balance?: Record<AssetPair, BalanceInfo>;
  isLoading: boolean;
  onChangeAsset?: (asset: AssetPair) => void;
  cardUrn?: string;
};

export function AccountBalanceSummary({
  balance,
  isLoading,
  onChangeAsset,
  cardUrn,
}: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: accountsData, isLoading: isLoadingAccounts } =
    useAccounts<AccountsBancolombiaResponse>({
      holder_urn: user?.urn ?? '',
      medium: 'bancolombia',
    });

  const linkedAccounts = cardUrn
    ? (accountsData?.data?.accounts?.filter(
        (account) => account.metadata?.card_urn === cardUrn,
      ) ?? [])
    : (accountsData?.data?.accounts ?? []);

  if (isLoading) {
    return <AccountBalanceSkeleton />;
  }

  if (!balance || Object.keys(balance).length === 0) {
    return <div>{m['card.account_balance_summary.no_balance_info']()}</div>;
  }

  const parsedBalances = Object.entries(balance).map(([pair, info]) => {
    const [assetCode, decimals] = pair.split('/') as [Asset, string];

    const assetMeta = ASSETS[assetCode] || {
      title: assetCode,
      icon: ksm,
    };

    const normalizedBalance = +info.current * 10 ** -parseInt(decimals, 10);
    const normalizedIn = +(info.in ?? '0') * 10 ** -parseInt(decimals, 10);
    const normalizedOut = +(info.out ?? '0') * 10 ** -parseInt(decimals, 10);
    const currencyKey = getCurrencyKeyFromBalanceCode(assetCode);

    const [units, cents] = parseFloat(normalizedBalance.toString())
      .toFixed(2)
      .split('.');

    let formattedAmount: string | number;
    let formattedIn: string | number;
    let formattedOut: string | number;

    if (currencyKey) {
      const locale = CURRENCIES[currencyKey].locale;
      formattedAmount =
        currencyKey === 'USD'
          ? units
          : Math.floor(normalizedBalance).toLocaleString(locale);
      formattedIn = Math.floor(normalizedIn).toLocaleString(locale);
      formattedOut = Math.floor(normalizedOut).toLocaleString(locale);
    } else {
      formattedAmount = normalizedBalance;
      formattedIn = normalizedIn;
      formattedOut = normalizedOut;
    }

    const suffix = currencyKey ? CURRENCIES[currencyKey].suffix : undefined;

    return {
      pair: pair as AssetPair,
      asset: assetMeta.title,
      icon: assetMeta.icon,
      amount: formattedAmount,
      cents: currencyKey === 'USD' ? cents : null,
      isEmpty: normalizedBalance === 0,
      code: assetCode,
      suffix,
      inAmount: formattedIn,
      outAmount: formattedOut,
    };
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 items-stretch">
      <div className="flex flex-col h-full">
        <div className="border border-input rounded-lg px-5 py-4 flex-grow flex flex-col">
          <h2 className="text-lg font-semibold mb-3">
            {m['card.account_balance_summary.wallets_title']()}
          </h2>

          <Tabs defaultValue={parsedBalances[0].asset}>
            <TabsList className="rounded-md p-1 h-auto">
              {parsedBalances.map(({ asset, icon, pair }) => (
                <TabsTrigger
                  key={asset}
                  value={asset}
                  onClick={() => onChangeAsset?.(pair)}
                  className="px-3"
                >
                  <img src={icon} alt="balance" />
                  {asset}
                </TabsTrigger>
              ))}
            </TabsList>

            {parsedBalances.map(
              ({
                asset,
                amount,
                cents,
                isEmpty,
                suffix,
                inAmount,
                outAmount,
              }) => (
                <TabsContent key={asset} value={asset}>
                  {isEmpty ? (
                    <>
                      <div className="mb-1">
                        <span className="text-4xl font-bold">$0</span>
                        <span className="text-xl align-top">,00</span>
                        {suffix && (
                          <span className="text-4xl font-bold ml-2">
                            {suffix}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col items-center justify-center">
                        <div className="relative w-32 h-32 mb-6">
                          <img src="/images/home/empty.png" alt="empty" />
                        </div>
                        <h3 className="text-xl font-semibold mb-2">
                          {m['card.account_balance_summary.empty_title']()}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-6">
                          {m[
                            'card.account_balance_summary.empty_description'
                          ]()}
                        </p>
                        <Button className="gap-2" variant="outline">
                          <Plus />
                          {m['card.account_balance_summary.empty_button']()}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-1">
                        <span className="text-4xl font-bold">${amount}</span>
                        {cents && (
                          <span className="text-xl align-top">,{cents}</span>
                        )}
                        {suffix && (
                          <span className="text-4xl font-bold ml-2">
                            {suffix}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        {m['card.account_balance_summary.balance_available']()}
                      </p>

                      <div className="flex gap-4 mt-2">
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground mb-1">
                            Ingresos
                          </p>
                          <p className="text-lg font-semibold text-green-600">
                            +${inAmount} {suffix ?? ''}
                          </p>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground mb-1">
                            Gastos
                          </p>
                          <p className="text-lg font-semibold text-red-600">
                            -${outAmount} {suffix ?? ''}
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </TabsContent>
              ),
            )}
          </Tabs>
        </div>
      </div>

      <div className="flex flex-col h-full relative">
        <div className="border border-input rounded-lg px-5 py-4 flex-grow flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">
              {m['card.account_balance_summary.linked_accounts_title']()}
            </h2>

            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate({ to: '/accounts' })}
            >
              {m['card.account_balance_summary.linked_accounts_view_all']()}
            </Button>
          </div>

          {isLoadingAccounts ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          ) : linkedAccounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-grow">
              <div className="relative w-32 h-32 mb-6">
                <img src="/images/home/account.png" alt="empty" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {m[
                  'card.account_balance_summary.linked_accounts_empty_title'
                ]()}
              </h3>
              <p className="text-sm text-muted-foreground mb-6 text-center">
                {m[
                  'card.account_balance_summary.linked_accounts_empty_description'
                ]()}
              </p>
              <Button
                className="gap-2"
                variant="outline"
                onClick={() => navigate({ to: '/accounts' })}
              >
                <Plus />
                {m['card.account_balance_summary.linked_accounts_add_button']()}
              </Button>
            </div>
          ) : (
            <div className="space-y-3 overflow-y-auto max-h-64">
              {linkedAccounts.map((account) => {
                const lastFour = account.details.reference_code
                  ? account.details.reference_code.slice(-4)
                  : '0000';

                return (
                  <button
                    type="button"
                    key={account.id}
                    className="w-full border border-input rounded-lg p-4 hover:border-purple-600 transition-colors text-left"
                    onClick={() => navigate({ to: '/accounts' })}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        <CreditCard className="w-5 h-5 text-muted-foreground" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          {account.metadata?.name && (
                            <span className="font-medium text-sm truncate">
                              {account.metadata.name}
                            </span>
                          )}
                          <div className="flex items-center gap-2 ml-2">
                            <span className="text-xs text-muted-foreground font-mono">
                              ••{lastFour}
                            </span>
                            <button
                              type="button"
                              className="flex items-center gap-1 text-xs bg-muted hover:bg-muted/80 px-2 py-1 rounded-md transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <img src={cop} alt="COP" className="w-4 h-4" />
                              <span>
                                {m[
                                  'card.account_balance_summary.asset_pesos'
                                ]()}
                              </span>
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccountBalanceSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 items-stretch">
      {/* Wallet Balance Skeleton */}
      <div className="flex flex-col h-full">
        <Skeleton className="h-6 w-32 mb-2" />
        <div className="border border-gray-800 rounded-lg p-5 flex-grow flex flex-col">
          <div className="mb-4">
            <div className="rounded-md p-1 h-auto bg-muted flex gap-1">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-20" />
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-1">
              <Skeleton className="h-12 w-40" />
            </div>
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      </div>

      <div className="flex flex-col h-full relative">
        <div className="flex items-center justify-between mb-2">
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-9 w-16" />
        </div>
        <div className="border border-gray-800 rounded-lg p-5 flex-grow flex flex-col relative blur-[1px] pointer-events-none opacity-75">
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="bg-black/80 text-white px-4 py-2 rounded-lg text-sm">
              {m['card.account_balance_summary.skeleton_coming_soon']()}
            </div>
          </div>

          <div className="mb-4">
            <div className="rounded-md p-1 h-auto bg-muted flex gap-1">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-20" />
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-1">
              <Skeleton className="h-12 w-24" />
            </div>
            <Skeleton className="h-4 w-28" />
          </div>
        </div>
      </div>
    </div>
  );
}
